/**
 * Godot's `texture_mipmap_bias` in three's programs. Godot builds the samplers of every 3D material
 * in a viewport with the bias as their mip level offset, on the four mipmapped filters only
 * (`material_storage.cpp:2454-2502`). WebGL2 has no per-sampler bias, so a program samples each such
 * slot with `texture(sampler, uv, bias)` from one shared uniform, and a `textureGrad` read with its
 * gradients scaled by `2^bias`, which shifts the mip level by the same amount. A pass writes the
 * uniform as it renders. A pass that draws 3D materials and skips `renderWithMipmapBias` samples at
 * the last value written.
 */
import { rewrittenShader, rewrittenThroughChunks } from '../shaderPatch/chunkPatch';
import { samplesMipChain } from '../../resources/textures/godotTextureFilter';
const BIAS_UNIFORM = 'godotMipmapBias';
const SAMPLE_HELPER = 'godotMipmapBiased';
const GRADIENT_SAMPLE_HELPER = 'godotMipmapBiasedGrad';
/**
 * The slots Godot's materials read through the viewport's samplers, under three's names: a
 * StandardMaterial3D's textures and the ones a glTF import adds. A lightmap has a sampler of its own,
 * and the displacement map is a vertex read.
 */
const BIASED_SLOTS = [
    'map',
    'alphaMap',
    'aoMap',
    'emissiveMap',
    'metalnessMap',
    'roughnessMap',
    'normalMap',
    'anisotropyMap',
    'clearcoatMap',
    'clearcoatNormalMap',
    'clearcoatRoughnessMap',
    'sheenColorMap',
    'sheenRoughnessMap',
    'specularColorMap',
    'specularIntensityMap',
    'transmissionMap',
    'thicknessMap',
    'iridescenceMap',
    'iridescenceThicknessMap',
];
const BIASED_SAMPLES = new RegExp(String.raw `(?:texture2D|textureGrad)\(\s*(${BIASED_SLOTS.join('|')})\s*,`, 'g');
/** Shared by every program, so one write reaches every material. Written only by this module. */
const biasUniform = { value: 0 };
/**
 * The bias of the pass rendering now, or null for a render of the main view. Written only by
 * `renderWithMipmapBias`.
 */
let activePassBias = null;
/** Runs `render` with `bias` as the bias of every program it draws: a SubViewport's pass. */
export function renderWithMipmapBias(bias, render) {
    const previousPassBias = activePassBias;
    const previousValue = biasUniform.value;
    activePassBias = bias;
    biasUniform.value = bias;
    try {
        render();
    }
    finally {
        activePassBias = previousPassBias;
        biasUniform.value = previousValue;
    }
}
/** The bias the render now under way samples at: its pass's, or the root viewport's. */
export function currentMipmapBias() {
    return biasUniform.value;
}
/**
 * Sets the bias for a render of a scene the main view draws, which takes the root viewport's
 * `rootBias`. A SubViewport pass that renders the same scene keeps its own bias.
 */
export function writeMainViewMipmapBias(rootBias) {
    biasUniform.value = activePassBias ?? rootBias;
}
const SAMPLER_FUNCTION = /* glsl */ `
uniform float ${BIAS_UNIFORM};
vec4 ${SAMPLE_HELPER}( sampler2D sampler, vec2 uv ) {
	return texture2D( sampler, uv, ${BIAS_UNIFORM} );
}
vec4 ${GRADIENT_SAMPLE_HELPER}( sampler2D sampler, vec2 uv, vec2 uvDx, vec2 uvDy ) {
	float scale = exp2( ${BIAS_UNIFORM} );
	return textureGrad( sampler, uv, uvDx * scale, uvDy * scale );
}
`;
/** `source` with every biased slot's sample routed through the helper. */
function biasedSamples(source) {
    return rewrittenShader(source, BIASED_SAMPLES, (read, slot) => {
        const helper = read.startsWith('textureGrad') ? GRADIENT_SAMPLE_HELPER : SAMPLE_HELPER;
        return `${helper}( ${slot},`;
    });
}
const GODOT_MIPMAP_BIAS = {
    cacheKey: 'godot-mipmap-bias',
    onBeforeCompile(shader) {
        shader.uniforms[BIAS_UNIFORM] = biasUniform;
        // `onBeforeCompile` sees a chunk's `#include`, not its samples.
        shader.fragmentShader = SAMPLER_FUNCTION + rewrittenThroughChunks(shader.fragmentShader, biasedSamples);
    },
};
/** The `userData` key of a material that draws through a viewport's 3D samplers. */
const VIEWPORT_SAMPLERS_KEY = 'godotViewportSamplers';
/**
 * The `userData` a 3D material records, so the bias reaches it. A canvas item draws through
 * the default samplers, which carry none.
 */
export function viewportSamplersUserData() {
    return { [VIEWPORT_SAMPLERS_KEY]: true };
}
/**
 * The injection a material's state asks for: the bias, when it records the viewport samplers and a
 * biased slot samples a mip chain. A material without a mip chain is not changed by a bias.
 */
export function mipmapBiasInjection(state) {
    const { userData } = state;
    if (typeof userData !== 'object' || userData === null)
        return undefined;
    if (userData[VIEWPORT_SAMPLERS_KEY] !== true)
        return undefined;
    // Indexed, not `.some`: no closure per material per render.
    for (let i = 0; i < BIASED_SLOTS.length; i++) {
        const texture = state[BIASED_SLOTS[i]];
        if (texture?.isTexture === true && samplesMipChain(texture))
            return GODOT_MIPMAP_BIAS;
    }
    return undefined;
}
//# sourceMappingURL=godotMipmapBias.js.map